import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Search, Star, Eye, Edit, MoreVertical, Grid, List, Plus, FileText, Video, Link as LinkIcon, Download, ExternalLink, Play, Sparkles, Filter, ArrowLeft, Trash2, ArrowLeft as ArrowLeftIcon, ArrowRight, X } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CompletePagination } from '@/components/ui/complete-pagination';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, getButtonColor, getBadgeColor } from '@/lib/utils';
import { useTeacher } from '@/context/TeacherContext';
import { useToast } from '@/hooks/use-toast';
import { EmptyState } from '@/components/ui/EmptyState';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export default function TeacherLibrary() {
  const navigate = useNavigate();
  const { teacherTheme } = useTeacher();
  const isDark = teacherTheme === 'dark';
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('question-library');
  const [questionLibrarySearch, setQuestionLibrarySearch] = useState('');
  const [questionLibraryFilters, setQuestionLibraryFilters] = useState({
    subject: 'all',
    chapter: 'all',
    difficulty: 'all',
    type: 'all'
  });
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(true);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [questionLibraryView, setQuestionLibraryView] = useState('grid'); // 'grid' or 'list'

  // Pagination state for Question Library
  const [questionCurrentPage, setQuestionCurrentPage] = useState(1);
  const [questionItemsPerPage, setQuestionItemsPerPage] = useState(10);

  // Fetch subjects and chapters for filters
  useEffect(() => {
    const fetchSubjectsChapters = async () => {
      try {
        const token = getAuthToken('teacher');
        if (!token) return;
        
        const res = await axios.get(apiUrl('v1/teachers/ai-tools/subjects-chapters/'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const apiSubjects = res?.data?.subjects || [];
        setSubjects(apiSubjects);
        
        // If a subject is selected, update chapters
        if (questionLibraryFilters.subject !== 'all') {
          const selectedSubject = apiSubjects.find(s => s.subject_id.toString() === questionLibraryFilters.subject);
          if (selectedSubject) {
            setChapters(selectedSubject.chapters || []);
          }
        }
      } catch (err) {
        console.error('Failed to load subjects and chapters', err);
      }
    };
    
    fetchSubjectsChapters();
  }, []);

  // Update chapters when subject filter changes
  useEffect(() => {
    if (questionLibraryFilters.subject !== 'all' && subjects.length > 0) {
      const selectedSubject = subjects.find(s => s.subject_id.toString() === questionLibraryFilters.subject);
      if (selectedSubject) {
        setChapters(selectedSubject.chapters || []);
      }
    } else {
      setChapters([]);
    }
  }, [questionLibraryFilters.subject, subjects]);

  // Fetch questions from API
  useEffect(() => {
    const fetchQuestions = async () => {
      setIsLoadingLibrary(true);
      try {
        const token = getAuthToken('teacher');
        if (!token) return;

        const params = new URLSearchParams();
        if (questionLibraryFilters.subject !== 'all') {
          params.append('subject', questionLibraryFilters.subject);
        }
        if (questionLibraryFilters.chapter !== 'all') {
          params.append('chapter', questionLibraryFilters.chapter);
        }
        if (questionLibraryFilters.difficulty !== 'all') {
          params.append('difficulty', questionLibraryFilters.difficulty);
        }
        if (questionLibraryFilters.type !== 'all') {
          params.append('question_type', questionLibraryFilters.type);
        }
        if (questionLibrarySearch) {
          params.append('search', questionLibrarySearch);
        }
        params.append('page', questionCurrentPage.toString());
        params.append('page_size', questionItemsPerPage.toString());

        const res = await axios.get(apiUrl(`v1/teachers/question-library/?${params.toString()}`), {
          headers: { Authorization: `Bearer ${token}` }
        });

        const apiQuestions = res?.data?.questions || [];
        const totalCount = res?.data?.total_count || 0;
        const totalPages = res?.data?.total_pages || 1;
        
        // Transform API questions to match existing format
        const transformedQuestions = apiQuestions.map((q) => {
          // Convert options from object to array if needed
          let optionsArray = null;
          if (q.options) {
            if (typeof q.options === 'object' && !Array.isArray(q.options)) {
              // Options are in format {A: 'text', B: 'text', ...}
              optionsArray = Object.values(q.options);
            } else if (Array.isArray(q.options)) {
              optionsArray = q.options;
            }
          }
          
          return {
            id: q.id,
            questionText: q.question_text,
            type: q.question_type,
            subject: q.subject_name,
            subjectId: q.subject_id,
            chapter: q.chapter_name,
            chapterId: q.chapter_id,
            difficulty: q.difficulty,
            options: optionsArray,
            correctAnswer: q.correct_option || q.correct_answer,
            correctAnswerText: q.correct_answer,
            explanation: q.explanation || '',
            tags: [],
            createdAt: q.created_at ? new Date(q.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '',
            isFavorite: false,
            isReusable: true
          };
        });

        setQuestionLibrary(transformedQuestions);
        setTotalLibraryCount(totalCount);
        
        // Update page if current page exceeds total pages
        if (questionCurrentPage > totalPages && totalPages > 0) {
          setQuestionCurrentPage(1);
        } else {
          // Ensure page doesn't go below 1
          if (questionCurrentPage < 1) {
            setQuestionCurrentPage(1);
          }
        }
      } catch (err) {
        console.error('Failed to load questions', err);
        toast({
          title: "Error",
          description: "Failed to load questions from library.",
          variant: "destructive",
        });
        setQuestionLibrary([]);
      } finally {
        setIsLoadingLibrary(false);
      }
    };

    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionLibraryFilters.subject, questionLibraryFilters.chapter, questionLibraryFilters.difficulty, questionLibraryFilters.type, questionLibrarySearch, questionCurrentPage, questionItemsPerPage]);


  // Question Library state (populated from API)
  const [questionLibrary, setQuestionLibrary] = useState([]);
  const [totalLibraryCount, setTotalLibraryCount] = useState(0);

  // Handle delete
  const handleDeleteClick = (question) => {
    setQuestionToDelete(question);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!questionToDelete) return;
    
    setIsDeleting(true);
    try {
      const token = getAuthToken('teacher');
      if (!token) {
        toast({
          title: "Error",
          description: "Authentication required",
          variant: "destructive",
        });
        return;
      }

      await axios.delete(apiUrl(`v1/teachers/question-library/${questionToDelete.id}/`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Deleted",
        description: "Question deleted successfully.",
      });

      try { trackEvent('teacher_library_question_deleted', attachUserProps({ question_id: questionToDelete?.id })); } catch(e) {}

      // Refresh the list by triggering useEffect
      setQuestionLibrary(prev => prev.filter(q => q.id !== questionToDelete.id));
      setDeleteConfirmOpen(false);
      setQuestionToDelete(null);
    } catch (err) {
      console.error('Failed to delete question', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to delete question. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle preview
  const handlePreviewClick = (question, allQuestions) => {
    setPreviewQuestions(allQuestions);
    const index = allQuestions.findIndex(q => q.id === question.id);
    setPreviewIndex(index >= 0 ? index : 0);
    setPreviewQuestion(question);
    try { trackEvent('teacher_library_preview_question', attachUserProps({ question_id: question.id })); } catch(e) {}
  };

  const handlePreviewClose = () => {
    setPreviewQuestion(null);
    setPreviewQuestions([]);
    setPreviewIndex(0);
  };

  const handlePreviewPrevious = () => {
    if (previewIndex > 0) {
      setPreviewIndex(previewIndex - 1);
      setPreviewQuestion(previewQuestions[previewIndex - 1]);
    }
  };

  const handlePreviewNext = () => {
    if (previewIndex < previewQuestions.length - 1) {
      setPreviewIndex(previewIndex + 1);
      setPreviewQuestion(previewQuestions[previewIndex + 1]);
    }
  };

  // Handle keyboard navigation in preview
  useEffect(() => {
    if (!previewQuestion) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && previewIndex > 0) {
        handlePreviewPrevious();
      } else if (e.key === 'ArrowRight' && previewIndex < previewQuestions.length - 1) {
        handlePreviewNext();
      } else if (e.key === 'Escape') {
        handlePreviewClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewQuestion, previewIndex, previewQuestions.length]);

  // Filtered questions (now handled by API)
  const filteredQuestions = questionLibrary;

  // Pagination (handled by API)
  const totalQuestionPages = Math.ceil(totalLibraryCount / questionItemsPerPage);
  const paginatedQuestions = filteredQuestions;


  const getDifficultyColor = (difficulty) => {
    return getBadgeColor(isDark);
  };

  const getTypeColor = (type) => {
    return getBadgeColor(isDark);
  };

  const renderQuestionLibrary = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Question Library</h2>
          <p className="text-muted-foreground mt-1">Browse, manage, and reuse your saved questions</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setQuestionLibraryView(questionLibraryView === 'grid' ? 'list' : 'grid')}
            className={cn("border shadow-sm transition-all", getButtonColor(isDark))}
          >
            {questionLibraryView === 'grid' ? <List className="w-4 h-4 mr-2" /> : <Grid className="w-4 h-4 mr-2" />}
            {questionLibraryView === 'grid' ? 'List View' : 'Grid View'}
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
            <CardHeader className={cn("rounded-t-2xl", isDark ? "bg-[hsl(var(--card-border))]/10" : "bg-[#F8F5F0]")}>
              <CardTitle className={cn("text-lg", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}> Filters</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {/* Search */}
              <div>
                <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Search</label>
                <div className="relative">
                  <Search className={cn("absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                  <input
                    type="text"
                    placeholder="Search questions..."
                    value={questionLibrarySearch}
                    onChange={(e) => setQuestionLibrarySearch(e.target.value)}
                    className={cn("w-full pl-10 pr-3 py-2 rounded-lg focus:ring-2 focus:border-transparent", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-[#F8F5F0] border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                  />
                </div>
              </div>

              {/* Subject Filter */}
              <div>
                <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Subject</label>
                <select
                  value={questionLibraryFilters.subject}
                  onChange={(e) => setQuestionLibraryFilters({ ...questionLibraryFilters, subject: e.target.value, chapter: 'all' })}
                  className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-[#F8F5F0] border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                >
                  <option value="all">All Subjects</option>
                  {subjects.map((subject) => (
                    <option key={subject.subject_id} value={subject.subject_id.toString()}>
                      {subject.subject_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chapter Filter */}
              <div>
                <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Chapter</label>
                <select
                  value={questionLibraryFilters.chapter}
                  onChange={(e) => setQuestionLibraryFilters({ ...questionLibraryFilters, chapter: e.target.value })}
                  disabled={questionLibraryFilters.subject === 'all'}
                  className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-[#F8F5F0] border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                >
                  <option value="all">All Chapters</option>
                  {chapters.map((chapter) => (
                    <option key={chapter.chapter_id} value={chapter.chapter_id.toString()}>
                      {chapter.chapter_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Difficulty Filter */}
              <div>
                <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Difficulty</label>
                <select
                  value={questionLibraryFilters.difficulty}
                  onChange={(e) => setQuestionLibraryFilters({ ...questionLibraryFilters, difficulty: e.target.value })}
                  className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-[#F8F5F0] border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                >
                  <option value="all">All Difficulties</option>
                  <option value="easy">Easy</option>
                  <option value="moderate">Moderate</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className={cn("block text-base font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Question Type</label>
                <select
                  value={questionLibraryFilters.type}
                  onChange={(e) => setQuestionLibraryFilters({ ...questionLibraryFilters, type: e.target.value })}
                  className={cn("w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-[#F8F5F0] border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}
                >
                  <option value="all">All Types</option>
                  <option value="mcq">MCQ</option>
                  <option value="true_false">True/False</option>
                  <option value="short_answer">Short Answer</option>
                  <option value="written_detailed">Written Detailed</option>
                </select>
              </div>

              {/* Stats */}
              <div className="pt-4 border-t border-[hsl(var(--card-border))]">
                <div className="text-sm text-muted-foreground">
                  <div className="flex justify-between mb-1">
                    <span>Total Questions:</span>
                    <span className="font-semibold text-foreground">{totalLibraryCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Showing:</span>
                    <span className="font-semibold text-foreground">{filteredQuestions.length}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Questions Grid/List */}
        <div className="lg:col-span-3">
          {isLoadingLibrary ? (
            <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
              <CardContent className="p-12">
                <div className="text-center">
                  <p className={cn("text-base font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Loading questions...</p>
                </div>
              </CardContent>
            </Card>
          ) : filteredQuestions.length === 0 ? (
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-12">
                <EmptyState
                  title="No questions found"
                  description="Try adjusting your filters or generate questions using AI Tools."
                />
              </CardContent>
            </Card>
          ) : questionLibraryView === 'grid' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paginatedQuestions.map((question) => (
                  <Card key={question.id} className={cn("border shadow-sm transition-colors", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] hover:border-[hsl(var(--gold-primary))]/50" : "bg-white border-[#E0D4BF] hover:border-[#C9A961]/50")}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn("px-2 py-1 rounded text-xs font-medium border", getTypeColor(question.type))}>
                            {question.type.toUpperCase().replace('_', ' ')}
                          </span>
                          <span className={cn("px-2 py-1 rounded text-xs font-medium border", getDifficultyColor(question.difficulty))}>
                            {question.difficulty}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDeleteClick(question)}
                            className={cn("p-1.5 rounded transition-colors text-red-500 hover:bg-red-500/10", isDark ? "hover:bg-red-500/20" : "")}
                            title="Delete question"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <p className={cn("text-base font-medium mb-3 line-clamp-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                        {question.questionText}
                      </p>

                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className={cn("text-sm font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{question.subject}</span>
                        <span className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>•</span>
                        <span className={cn("text-sm font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{question.chapter}</span>
                      </div>

                      <div className={cn("flex items-center gap-2 pt-3 border-t", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                        <Button 
                          size="sm" 
                          className={cn("flex-1 text-sm", getButtonColor(isDark))}
                          onClick={() => handlePreviewClick(question, filteredQuestions)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className={cn("text-sm")}
                          onClick={() => handleDeleteClick(question)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <CompletePagination
                currentPage={questionCurrentPage}
                totalPages={totalQuestionPages}
                onPageChange={setQuestionCurrentPage}
                itemsPerPage={questionItemsPerPage}
                onItemsPerPageChange={setQuestionItemsPerPage}
                totalItems={totalLibraryCount}
                showItemsPerPage={true}
              />
            </>
          ) : (
            <>
              <div className="space-y-3">
                {paginatedQuestions.map((question) => (
                  <Card key={question.id} className={cn("border shadow-sm transition-colors", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] hover:border-[hsl(var(--gold-primary))]/50" : "bg-white border-[#E0D4BF] hover:border-[#C9A961]/50")}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={cn("px-2 py-1 rounded text-xs font-medium border", getTypeColor(question.type))}>
                              {question.type.toUpperCase().replace('_', ' ')}
                            </span>
                            <span className={cn("px-2 py-1 rounded text-xs font-medium border", getDifficultyColor(question.difficulty))}>
                              {question.difficulty}
                            </span>
                            <span className={cn("text-sm font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{question.subject} • {question.chapter}</span>
                          </div>
                          <p className={cn("text-base font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{question.questionText}</p>
                          <div className={cn("flex items-center gap-4 text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            <span>Created: {question.createdAt}</span>
                            {question.tags.map((tag, idx) => (
                              <span key={idx} className={cn("px-2 py-0.5 rounded text-[10px] font-medium", isDark ? "bg-[hsl(var(--card-border))]/30 text-[hsl(var(--foreground))]" : "bg-[#E0D4BF]/30 text-[#2B2B2B]")}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            className={cn("text-sm", getButtonColor(isDark))}
                            onClick={() => handlePreviewClick(question, filteredQuestions)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            className={cn("text-sm")}
                            onClick={() => handleDeleteClick(question)}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <CompletePagination
                currentPage={questionCurrentPage}
                totalPages={totalQuestionPages}
                onPageChange={setQuestionCurrentPage}
                itemsPerPage={questionItemsPerPage}
                onItemsPerPageChange={setQuestionItemsPerPage}
                totalItems={totalLibraryCount}
                showItemsPerPage={true}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={cn("flex flex-col h-full p-6 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/teacher/ai-tools')}
              className={cn("p-2 rounded-lg transition-colors", isDark ? "hover:bg-[hsl(var(--card-border))]/50 text-[hsl(var(--foreground))]" : "hover:bg-[#F0E8D8] text-[#2B2B2B]")}
              title="Back to AI Tools"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Library</h1>
              <p className={cn("mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Manage your questions and reusable content</p>
            </div>
          </div>
        </div>

        {/* Library Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={cn("grid w-full grid-cols-1 rounded-3xl p-1 shadow-sm", isDark ? "bg-gradient-to-r from-[hsl(var(--card))] to-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-gradient-to-r from-[#F0E8D8] to-[#EDE3D2] border border-[#E0D4BF]")}>
            <TabsTrigger
              value="question-library"
              className={cn("rounded-2xl text-sm font-medium p-2 transition-all flex items-center justify-center gap-2", isDark ? "data-[state=active]:bg-gradient-to-r data-[state=active]:from-[hsl(var(--gold-primary))] data-[state=active]:to-[hsl(var(--gold-light))] data-[state=active]:text-white data-[state=active]:shadow-md" : "data-[state=active]:bg-[#2B2B2B] data-[state=active]:text-white data-[state=active]:shadow-md")}
            >
              <FileText className="w-4 h-4" />
              <span>Question Library</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="question-library" className="mt-6">
            {renderQuestionLibrary()}
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Modal */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmOpen(false);
            setQuestionToDelete(null);
          }
        }}>
          <AlertDialogContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
            <AlertDialogHeader>
              <AlertDialogTitle className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>
                Delete Question
              </AlertDialogTitle>
              <AlertDialogDescription className={cn("text-sm", isDark ? "text-white" : "text-[#6B6B6B]")}>
                Are you sure you want to delete this question? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setQuestionToDelete(null);
                }}
                disabled={isDeleting}
                className={cn(isDark ? "text-white hover:text-white" : "")}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Preview Modal */}
        {previewQuestion && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handlePreviewClose}>
            <div 
              className={cn(
                "relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl",
                isDark ? "bg-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-white border border-[#E0D4BF]"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={cn("flex items-center justify-between p-6 border-b", isDark ? "border-[hsl(var(--card-border))] bg-[hsl(var(--card-border))]/10" : "border-[#E0D4BF] bg-[#F8F5F0]")}>
                <div className="flex items-center gap-4">
                  <h2 className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                    Question Preview
                  </h2>
                  <span className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                    {previewIndex + 1} of {previewQuestions.length}
                  </span>
                </div>
                <button
                  onClick={handlePreviewClose}
                  className={cn("p-2 rounded-lg transition-colors", isDark ? "hover:bg-[hsl(var(--card-border))]/50 text-[hsl(var(--foreground))]" : "hover:bg-[#E0D4BF] text-[#2B2B2B]")}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="space-y-6">
                  {/* Question Info */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={cn("px-3 py-1 rounded-full text-xs font-medium border", getTypeColor(previewQuestion.type))}>
                      {previewQuestion.type.toUpperCase().replace('_', ' ')}
                    </span>
                    <span className={cn("px-3 py-1 rounded-full text-xs font-medium border", getDifficultyColor(previewQuestion.difficulty))}>
                      {previewQuestion.difficulty}
                    </span>
                    <span className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                      {previewQuestion.subject} • {previewQuestion.chapter}
                    </span>
                  </div>

                  {/* Question Text */}
                  <div>
                    <h3 className={cn("text-lg font-semibold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                      Question
                    </h3>
                    <p className={cn("text-base", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                      {previewQuestion.questionText}
                    </p>
                  </div>

                  {/* Options (for MCQ/True-False) */}
                  {(previewQuestion.type === 'mcq' || previewQuestion.type === 'true_false') && previewQuestion.options && (
                    <div>
                      <h3 className={cn("text-lg font-semibold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                        Options
                      </h3>
                      <div className="space-y-2">
                        {previewQuestion.options.map((option, index) => (
                          <div
                            key={index}
                            className={cn(
                              "p-3 rounded-lg border",
                              previewQuestion.correctAnswer === String.fromCharCode(65 + index)
                                ? isDark ? "bg-green-900/20 border-green-500/50" : "bg-green-50 border-green-500"
                                : isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]"
                            )}
                          >
                            <span className={cn("font-semibold mr-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                              {String.fromCharCode(65 + index)})
                            </span>
                            <span className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                              {option}
                            </span>
                            {previewQuestion.correctAnswer === String.fromCharCode(65 + index) && (
                              <span className="ml-2 text-green-500 font-semibold">✓ Correct</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Correct Answer */}
                  <div>
                    <h3 className={cn("text-lg font-semibold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                      Correct Answer
                    </h3>
                    <div className={cn("p-4 rounded-lg", isDark ? "bg-[hsl(var(--card-border))]/20" : "bg-green-50")}>
                      <p className={cn("text-base", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                        {previewQuestion.correctAnswerText || previewQuestion.correctAnswer}
                      </p>
                    </div>
                  </div>

                  {/* Explanation */}
                  {previewQuestion.explanation && (
                    <div>
                      <h3 className={cn("text-lg font-semibold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                        Explanation
                      </h3>
                      <p className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                        {previewQuestion.explanation}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer with Navigation */}
              <div className={cn("flex items-center justify-between p-6 border-t", isDark ? "border-[hsl(var(--card-border))] bg-[hsl(var(--card-border))]/10" : "border-[#E0D4BF] bg-[#F8F5F0]")}>
                <Button
                  onClick={handlePreviewPrevious}
                  disabled={previewIndex === 0}
                  className={cn("flex items-center gap-2", getButtonColor(isDark))}
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Previous
                </Button>
                <span className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                  Use ← → arrow keys to navigate
                </span>
                <Button
                  onClick={handlePreviewNext}
                  disabled={previewIndex >= previewQuestions.length - 1}
                  className={cn("flex items-center gap-2", getButtonColor(isDark))}
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
